import React from 'react';
import { normalizeCrop, croppedImageStyle } from './CroppedImage';

// One visit photo, framed to a particular dish (#198).
//
// A single photo can now be of several dishes — a table shot holding quiche,
// toast and coffee is ONE upload with three attributions, not three uploads.
// Each attribution carries its own crop, so the same image can appear in each
// dish's row showing that dish rather than the whole table.
//
// Crops are normalized 0..1 fractions of the image, never pixels: clients resize
// and re-encode before upload, which invalidates pixel offsets but leaves
// fractions correct. No crop (or no attribution for this dish) means the whole
// image — which is what every photo taken before #198 means, and a perfectly
// good answer that the tap-to-place capture UI lets people keep.
//
// Nothing here mutates the image. The crop is presentation metadata applied at
// render; the stored object is always the original, whole photo.
//
// Shared by all three surfaces that show a photo against a dish — the profile's
// visit list, the grouped visit review card, and the flat review list — because
// the framing arithmetic is exactly the kind of thing that rots when it is
// copy-pasted three times (see #67 on the duplicated restaurant card).
//
// #234: that arithmetic now lives in CroppedImage, because the public menu and
// Food pages needed it too once the bridge's crops were finally served. This
// file keeps only what is specific to a VISIT photo — finding the attribution
// for a given dish, and opening the original on click.

const DEFAULT_SIZE = 80;

/** The crop recorded for `visitDishId`, or null for "show the whole image". */
export function cropForDish(photo, visitDishId) {
  if (!photo || visitDishId == null) return null;
  // No attribution for this dish is "whole image" too, and is distinct from an
  // attribution that carries no crop — both render the same, neither is an error.
  const match = (photo.dishes || []).find((d) => d.visit_dish_id === visitDishId);
  return normalizeCrop(match);
}

export default function DishPhoto({
  photo,
  visitDishId = null,
  alt = 'Visit',
  size = DEFAULT_SIZE,
  onClick,
  children,
}) {
  const crop = cropForDish(photo, visitDishId);
  // Clicking always opens the ORIGINAL, uncropped photo. The crop says which
  // dish this thumbnail is about; it does not claim the rest of the shot is not
  // there, and hiding it at full size would be a lie about what was uploaded.
  const open = onClick || (() => window.open(photo.url, '_blank', 'noopener'));

  const frame = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '6px',
    border: '1px solid #dee2e6',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
    flex: '0 0 auto',
  };

  if (!crop) {
    return (
      <div className="position-relative" style={{ ...frame, border: 'none' }}>
        <img
          src={photo.url}
          alt={alt}
          onClick={open}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            borderRadius: '6px', cursor: 'pointer', border: '1px solid #dee2e6',
          }}
        />
        {children}
      </div>
    );
  }

  const scaled = { ...croppedImageStyle(crop), cursor: 'pointer' };

  return (
    <div className="position-relative" style={frame} onClick={open} title={alt}>
      <img src={photo.url} alt={alt} style={scaled} />
      {children}
    </div>
  );
}
